import React, { useState, useEffect, useRef } from 'react';
import axios from '../../api/axios';
import Layout from '../../common/components/Navbar';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Box,
  Button,
  Title,
  Paper,
  Grid,
  Table,
  Select,
  Divider,
  Text,
  Group,
  Container,
  Stack,
  Loader
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications'; 
import '@mantine/dates/styles.css';
import { IconReportAnalytics, IconPrinter, IconDownload } from '@tabler/icons-react';

const Reports = () => {
  const [reportType, setReportType] = useState('financial_summary');
  const [dateRange, setDateRange] = useState([null, null]); 
  const [residents, setResidents] = useState([]);
  const [selectedResident, setSelectedResident] = useState('');
  const [reportData, setReportData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const componentRef = useRef(null);

  // --- UNIFIED PDF GENERATION (PRINT & DOWNLOAD) ---
  const handleProcessReport = async (action) => {
    const input = componentRef.current;
    if (!input) {
        notifications.show({ title: 'Error', message: 'Report content not found.', color: 'red' });
        return;
    }

    setDownloading(true);
    try {
        const canvas = await html2canvas(input, {
            scale: 2, 
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff'
        });

        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth(); 
        const pdfHeight = pdf.internal.pageSize.getHeight(); 

        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        
        const ratio = pdfWidth / imgWidth;
        const scaledHeight = imgHeight * ratio;

        const imgData = canvas.toDataURL('image/png');

        if (scaledHeight > pdfHeight) {
             pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, scaledHeight);
        } else {
             pdf.addImage(imgData, 'PNG', 0, 10, pdfWidth, scaledHeight); 
        }

        if (action === 'download') {
            pdf.save(`Report_${reportType}_${new Date().toISOString().split('T')[0]}.pdf`);
            notifications.show({ title: 'Success', message: 'PDF Downloaded', color: 'green' });
        } else if (action === 'print') {
            pdf.autoPrint();
            const blob = pdf.output('bloburl');
            window.open(blob, '_blank');
        }

    } catch (error) {
        console.error("PDF Error:", error);
        notifications.show({ title: 'Error', message: 'Failed to process document', color: 'red' });
    }
    setDownloading(false);
  };

  useEffect(() => {
    const fetchResidents = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const config = { headers: { Authorization: `Bearer ${user.token}` } };
        const { data } = await axios.get('/api/residents', config);
        setResidents(data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchResidents();
  }, []);

  const generateReport = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      notifications.show({ title: 'Validation Error', message: 'Please select a valid date range.', color: 'red' });
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      
      const payload = {
        type: reportType,
        startDate: dateRange[0], 
        endDate: dateRange[1],   
        residentId: selectedResident
      };

      const { data } = await axios.post('/api/reports/generate', payload, config);
      setReportData(data);
      notifications.show({ title: 'Success', message: 'Report generated successfully.', color: 'green' });

    } catch (error) {
      console.error(error);
      notifications.show({ title: 'Error', message: 'Failed to generate report.', color: 'red' });
    }
  };

  const formatCurrency = (amount) => {
    const num = Number(amount);
    return isNaN(num) ? '0.00' : num.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const renderTableHead = () => {
    switch (reportType) {
      case 'collection_report':
        return (
          <Table.Tr>
            <Table.Th>Date</Table.Th>
            <Table.Th>Payor</Table.Th>
            <Table.Th>Particulars</Table.Th>
            <Table.Th>Method</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Amount</Table.Th>
          </Table.Tr>
        );
      case 'expense_report':
        return (
          <Table.Tr>
            <Table.Th>Date</Table.Th>
            <Table.Th>Title</Table.Th>
            <Table.Th>Category</Table.Th>
            <Table.Th>Particulars</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Amount</Table.Th>
          </Table.Tr>
        );
      case 'customer_ledger':
        return (
          <Table.Tr>
            <Table.Th>Date</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Ref No.</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Debit</Table.Th>
            <Table.Th style={{ textAlign: 'right' }}>Credit</Table.Th>
            <Table.Th style={{ textAlign: 'right', fontWeight: 'bold' }}>Balance</Table.Th>
          </Table.Tr>
        );
      default:
        return <Table.Tr><Table.Th>Summary View Only</Table.Th></Table.Tr>;
    }
  };

  const renderTableBody = () => {
    if (!reportData || !reportData.details) return null;

    return reportData.details.map((row, index) => {
      if (reportType === 'collection_report') {
        return (
          <Table.Tr key={index}>
            <Table.Td>{new Date(row.date).toLocaleDateString()}</Table.Td>
            <Table.Td>
              <Text size="sm" fw={700}>{row.resident}</Text>
              <Text size="xs" c="dimmed">{row.blockLot}</Text>
            </Table.Td>
            <Table.Td>{row.description}</Table.Td>
            <Table.Td style={{ textTransform: 'capitalize' }}>{row.method}</Table.Td>
            <Table.Td style={{ textAlign: 'right' }}>₱{formatCurrency(row.amount)}</Table.Td>
          </Table.Tr>
        );
      }
      if (reportType === 'expense_report') {
        return (
          <Table.Tr key={index}>
            <Table.Td>{new Date(row.date).toLocaleDateString()}</Table.Td>
            <Table.Td>{row.title}</Table.Td>
            <Table.Td style={{ textTransform: 'capitalize' }}>{row.category}</Table.Td>
            <Table.Td>{row.particulars}</Table.Td>
            <Table.Td style={{ textAlign: 'right' }}>₱{formatCurrency(row.amount)}</Table.Td>
          </Table.Tr>
        );
      }
      if (reportType === 'customer_ledger') {
        return (
          <Table.Tr key={index}>
            <Table.Td>{new Date(row.date).toLocaleDateString()}</Table.Td>
            <Table.Td>{row.description}</Table.Td>
            <Table.Td>{row.ref}</Table.Td>
            <Table.Td style={{ textAlign: 'right' }}>{row.debit > 0 ? `₱${formatCurrency(row.debit)}` : '-'}</Table.Td>
            <Table.Td style={{ textAlign: 'right' }}>{row.credit > 0 ? `₱${formatCurrency(row.credit)}` : '-'}</Table.Td>
            <Table.Td style={{ textAlign: 'right', fontWeight: 'bold' }}>₱{formatCurrency(row.balance)}</Table.Td>
          </Table.Tr>
        );
      }
      return null;
    });
  };

  // --- REUSABLE REPORT CONTENT (Used for both Hidden Print & Visual Preview) ---
  const renderReportContent = () => (
    <Stack gap="lg">
      {/* Report Header */}
      <Box style={{ textAlign: 'center', marginBottom: 20 }}>
          <Title order={3} style={{ textTransform: 'uppercase' }}>BANCOM LIFE HOMEOWNERS ASSOCIATION, INC.</Title>
          <Text>San Mateo, Rizal</Text>
          <Divider my="sm" color="gray" size="sm" />
          <Title order={4} style={{ textTransform: 'uppercase', letterSpacing: 1, marginTop: 20 }}>{reportData.title}</Title>
          <Text size="sm" c="dimmed">
          Period: {new Date(reportData.startDate).toLocaleDateString()} - {new Date(reportData.endDate).toLocaleDateString()}
          </Text>
      </Box>

      {/* Financial Summary View */}
      {reportType === 'financial_summary' ? (
          <Box>
              <Grid gutter="lg">
                  {/* Income Table */}
                  <Grid.Col span={6}>
                      <Text size="md" fw={700} mb="xs">INCOME</Text>
                      <Table withTableBorder withColumnBorders>
                          <Table.Thead>
                              <Table.Tr style={{ backgroundColor: '#f8f9fa' }}>
                                  <Table.Th>Category</Table.Th>
                                  <Table.Th style={{ textAlign: 'right' }}>Amount</Table.Th>
                              </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                              {reportData.collectionBreakdown && reportData.collectionBreakdown.map((item, idx) => (
                                  <Table.Tr key={idx}>
                                      <Table.Td>{item.category}</Table.Td>
                                      <Table.Td style={{ textAlign: 'right' }}>₱{formatCurrency(item.amount)}</Table.Td>
                                  </Table.Tr>
                              ))}
                              <Table.Tr style={{ backgroundColor: '#e9ecef' }}>
                                  <Table.Td style={{ fontWeight: 'bold' }}>TOTAL INCOME</Table.Td>
                                  <Table.Td style={{ textAlign: 'right', fontWeight: 'bold' }}>₱{formatCurrency(reportData.totalCollections)}</Table.Td>
                              </Table.Tr>
                          </Table.Tbody>
                      </Table>
                  </Grid.Col>

                  {/* Expense Table */}
                  <Grid.Col span={6}>
                      <Text size="md" fw={700} mb="xs">EXPENSES</Text>
                      <Table withTableBorder withColumnBorders>
                          <Table.Thead>
                              <Table.Tr style={{ backgroundColor: '#f8f9fa' }}>
                                  <Table.Th>Category</Table.Th>
                                  <Table.Th style={{ textAlign: 'right' }}>Amount</Table.Th>
                              </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                              {reportData.expenseBreakdown && reportData.expenseBreakdown.map((item, idx) => (
                                  <Table.Tr key={idx}>
                                      <Table.Td>{item.category}</Table.Td>
                                      <Table.Td style={{ textAlign: 'right' }}>₱{formatCurrency(item.amount)}</Table.Td>
                                  </Table.Tr>
                              ))}
                              <Table.Tr style={{ backgroundColor: '#e9ecef' }}>
                                  <Table.Td style={{ fontWeight: 'bold' }}>TOTAL EXPENSE</Table.Td>
                                  <Table.Td style={{ textAlign: 'right', fontWeight: 'bold' }}>₱{formatCurrency(reportData.totalExpenses)}</Table.Td>
                              </Table.Tr>
                          </Table.Tbody>
                      </Table>
                  </Grid.Col>
              </Grid>
          </Box>
      ) : (
          <>
          {/* Other Report Types */}
          {reportType === 'customer_ledger' && reportData.residentDetails && (
              <Box mb="md">
              <Text><strong>Resident:</strong> {reportData.residentDetails.lastName}, {reportData.residentDetails.firstName}</Text>
              <Text><strong>Address:</strong> Blk {reportData.residentDetails.address.block} Lot {reportData.residentDetails.address.lot}</Text>
              </Box>
          )}

          <Paper withBorder>
              <Table striped highlightOnHover>
              <Table.Thead style={{ backgroundColor: '#f8f9fa' }}>
                  {renderTableHead()}
              </Table.Thead>
              <Table.Tbody>
                  {renderTableBody()}
                  {(reportType === 'collection_report' || reportType === 'expense_report') && (
                  <Table.Tr>
                      <Table.Td colSpan={reportType === 'collection_report' ? 4 : 3} style={{ fontWeight: 'bold', textAlign: 'right' }}>
                      TOTAL:
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                      ₱{formatCurrency(reportData.total)}
                      </Table.Td>
                  </Table.Tr>
                  )}
              </Table.Tbody>
              </Table>
          </Paper>
          </>
      )}

      {/* Footer Signatories */}
      <Group justify="space-between" mt={80} align="flex-end">
          <Box style={{ textAlign: 'center', width: 200 }}>
              <Text fw={700} style={{ borderBottom: '1px solid black', paddingBottom: 2, marginBottom: 4, display: 'block' }}>
                  {reportData.generatedBy || 'Admin'}
              </Text>
              <Text size="sm">Prepared By</Text>
          </Box>

          <Box style={{ textAlign: 'center', width: 200 }}>
              <Box style={{ borderBottom: '1px solid black', height: 24, marginBottom: 4 }} />
              <Text size="sm">Approved By</Text>
          </Box>
      </Group>

      {/* Footer Note */}
      <Text size="xs" c="dimmed" align="center" mt="xl" style={{ fontStyle: 'italic' }}>
          Report Generated on: {new Date().toLocaleString('en-PH')}
      </Text>
    </Stack>
  );

  const residentData = residents.map(res => ({
    value: res._id,
    label: `${res.lastName}, ${res.firstName}`
  }));

  return (
    <Layout>
      <Group justify="space-between" mb="lg">
        <Title order={2}>Reports Generator</Title>
      </Group>

      <Paper p="md" mb="lg" withBorder>
        <Grid align="flex-end">
          <Grid.Col span={reportType === 'customer_ledger' ? 3 : 4}>
            <Select 
              label="Report Type" 
              value={reportType} 
              onChange={(value) => {
                setReportType(value);
                setReportData(null);
                setDateRange([null, null]); 
              }}
              data={[
                { value: 'financial_summary', label: 'Financial Summary' },
                { value: 'collection_report', label: 'Daily/Period Collection' },
                { value: 'expense_report', label: 'Expense Report' },
                { value: 'customer_ledger', label: 'Customer Ledger' }
              ]}
              allowDeselect={false}
            />
          </Grid.Col>

          {reportType === 'customer_ledger' && (
            <Grid.Col span={3}>
              <Select 
                label="Select Resident" 
                value={selectedResident} 
                onChange={setSelectedResident}
                data={residentData}
                searchable
                placeholder="Search Resident"
              />
            </Grid.Col>
          )}

          <Grid.Col span={4}>
            <DatePickerInput
              type="range"
              label="Date Range"
              placeholder="Select range"
              value={dateRange}
              onChange={setDateRange}
              maxDate={new Date()}
              allowSingleDateInRange={reportType === 'collection_report'}
              clearable
              color="green" 
              // ADDED: Forces concise display format (e.g., 11/20/2025)
              valueFormat="MM/DD/YYYY"
              getDayProps={(date) => {
                try {
                    const d = new Date(date);
                    const start = dateRange[0] ? new Date(dateRange[0]) : null;
                    const end = dateRange[1] ? new Date(dateRange[1]) : null;

                    const isStart = start && d.toDateString() === start.toDateString();
                    const isEnd = end && d.toDateString() === end.toDateString();
                    
                    if (isStart || isEnd) {
                        return {
                            style: { 
                                backgroundColor: 'var(--mantine-color-green-filled)', 
                                color: 'var(--mantine-color-white)',
                                fontWeight: 'bold',
                                borderRadius: '4px'
                            },
                            selected: true
                        };
                    }
                } catch (e) {
                    return {};
                }
                return {};
              }}
              />
          </Grid.Col>
          
          <Grid.Col span={2}>
            <Button 
                fullWidth 
                leftSection={<IconReportAnalytics size={18} />} 
                onClick={generateReport}
                disabled={!dateRange[0] || !dateRange[1]} 
            >
              Generate
            </Button>
          </Grid.Col>
        </Grid>
      </Paper>

      {/* ACTIONS BUTTONS */}
      {reportData && (
        <Group justify="flex-end" mb="md" gap="sm">
          <Button 
            variant="default" 
            leftSection={downloading ? <Loader size={14} /> : <IconDownload size={18} />} 
            onClick={() => handleProcessReport('download')}
            disabled={downloading}
          >
            {downloading ? 'Processing...' : 'Download PDF'}
          </Button>
          
          <Button 
            variant="outline" 
            leftSection={downloading ? <Loader size={14} /> : <IconPrinter size={18} />} 
            onClick={() => handleProcessReport('print')}
            disabled={downloading}
          >
            Print Report
          </Button>
        </Group>
      )}

      {/* OFF-SCREEN RENDER AREA FOR PRINTING/PDF (HIDDEN BUT DOM-ACTIVE) */}
      <div style={{ height: reportData ? '0px' : '0px', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: '-9999px', width: '210mm' }}>
            <div 
                ref={componentRef} 
                style={{ 
                    backgroundColor: 'white', 
                    padding: '40px', 
                    width: '210mm', 
                    minHeight: '297mm',
                    margin: '0 auto',
                }}
            >
            {reportData && renderReportContent()}
            </div>
        </div>
      </div>
      
      {/* VISUAL PREVIEW ON SCREEN */}
      {reportData && (
        <Container size="lg" mt="xl" mb="xl">
            <Divider label="Report Preview" labelPosition="center" mb="lg" />
            {/* ADDED: Scroll wrapper and minWidth to fix mobile responsiveness */}
            <div style={{ overflowX: 'auto', background: 'white', padding: '40px', borderRadius: '8px', border: '1px solid #ddd' }}>
               <div style={{ minWidth: '800px' }}>
                   {renderReportContent()}
               </div>
            </div>
        </Container>
      )}

    </Layout>
  );
};

export default Reports;